import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  Building,
  DollarSign,
  Home,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  matching: "Matching AI (ricerca strutture)",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return iso;
  }
}

export default function CasadiriposoperteAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState("ai");
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageMonth, setUsageMonth] = useState("");

  const [operations, setOperations] = useState({
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
  });
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsMonth, setOperationsMonth] = useState("");
  const [operationsFeature, setOperationsFeature] = useState("");

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/overview`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento overview");
      }
      setOverview(await res.json());
    } catch (e) {
      toast.error(e.message || "Errore overview CasaDiRiposoPerTe");
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [token]);

  const loadUsage = useCallback(
    async (month = "") => {
      setUsageLoading(true);
      try {
        const qs = month ? `?month=${encodeURIComponent(month)}` : "";
        const res = await fetch(
          `${API}/api/admin/casadiriposoperte/ai-usage${qs}`,
          { headers }
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || "Errore caricamento costi AI");
        }
        setUsage(await res.json());
      } catch (e) {
        toast.error(e.message || "Errore costi AI");
        setUsage(null);
      } finally {
        setUsageLoading(false);
      }
    },
    [token]
  );

  const loadOperations = useCallback(
    async (month = "", feature = "", offset = 0) => {
      setOperationsLoading(true);
      try {
        const params = new URLSearchParams({
          limit: "50",
          offset: String(offset),
        });
        if (month) params.set("month", month);
        if (feature) params.set("feature", feature);
        const res = await fetch(
          `${API}/api/admin/casadiriposoperte/ai-operations?${params}`,
          { headers }
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || "Errore caricamento operazioni AI");
        }
        setOperations(await res.json());
      } catch (e) {
        toast.error(e.message || "Errore operazioni AI");
      } finally {
        setOperationsLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeTab === "ai") {
      loadUsage(usageMonth);
      loadOperations(operationsMonth, operationsFeature, 0);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const featureLabel = (feature) =>
    FEATURE_LABELS[feature] ||
    usage?.feature_labels?.[feature] ||
    feature ||
    "—";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Home className="w-5 h-5 text-rose-600" />
            CasaDiRiposoPerTe
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Sezione dedicata al portale CasaDiRiposoPerTe: monitoraggio ricerche AI e,
            in futuro, altre funzioni di backend.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            loadOverview();
            if (activeTab === "ai") {
              loadUsage(usageMonth);
              loadOperations(operationsMonth, operationsFeature, operations.offset || 0);
            }
          }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Stato collegamento</CardTitle>
        </CardHeader>
        <CardContent>
          {overviewLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : overview ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-slate-500 mb-1">Database</p>
                <div className="flex items-center gap-2">
                  <Badge variant={overview.connected ? "default" : "destructive"}>
                    {overview.connected ? "Connesso" : "Non raggiungibile"}
                  </Badge>
                  <span className="text-sm text-slate-600 font-mono">
                    {overview.db_name}
                  </span>
                </div>
                {overview.error && (
                  <p className="text-xs text-rose-600 mt-2">{overview.error}</p>
                )}
              </div>
              <div className="rounded-lg bg-slate-50 p-4 text-center">
                <p className="text-2xl font-bold flex items-center justify-center gap-2">
                  <Building className="w-5 h-5 text-slate-400" />
                  {overview.active_structures ?? "—"}
                </p>
                <p className="text-xs text-slate-500">Strutture attive</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 text-center">
                <p className="text-2xl font-bold">
                  {overview.ai_usage?.total_calls ?? 0}
                </p>
                <p className="text-xs text-slate-500">
                  Ricerche AI ({overview.month || "mese corrente"})
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Overview non disponibile.</p>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 h-auto">
          <TabsTrigger value="ai" className="gap-1.5">
            <Sparkles className="w-4 h-4" />
            Ricerche AI
          </TabsTrigger>
          <TabsTrigger value="future" className="gap-1.5">
            <Activity className="w-4 h-4" />
            Altre funzioni
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-violet-600" />
                Monitoraggio costi
              </CardTitle>
              <p className="text-sm text-slate-500">
                Consumo token e costo stimato delle ricerche Matching AI su
                CasaDiRiposoPerTe (stesso account OpenAI di Affidya).
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-6">
                <div>
                  <Label>Mese (YYYY-MM)</Label>
                  <Input
                    type="month"
                    className="mt-1 w-44"
                    value={usageMonth}
                    onChange={(e) => setUsageMonth(e.target.value)}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => loadUsage(usageMonth)}
                  disabled={usageLoading}
                >
                  {usageLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Aggiorna
                </Button>
              </div>

              {usageLoading && !usage ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : usage ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">{usage.total_calls}</p>
                      <p className="text-xs text-slate-500">Chiamate</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">
                        {usage.total_tokens?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">Token</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-center">
                      <p className="text-2xl font-bold">
                        ${usage.total_cost_usd?.toFixed(4)}
                      </p>
                      <p className="text-xs text-slate-500">Costo stimato</p>
                    </div>
                  </div>
                  {usage.by_feature?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                        Per funzione
                      </p>
                      <ul className="text-sm space-y-1">
                        {usage.by_feature.map((row) => (
                          <li
                            key={row.feature}
                            className="flex justify-between text-slate-600"
                          >
                            <span>{featureLabel(row.feature)}</span>
                            <span>
                              {row.calls} chiamate — ${row.cost_usd}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {usage.by_model?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                        Per modello
                      </p>
                      <ul className="text-sm space-y-1">
                        {usage.by_model.map((row) => (
                          <li
                            key={row.model}
                            className="flex justify-between text-slate-600"
                          >
                            <span>{row.model}</span>
                            <span>
                              {row.calls} chiamate — ${row.cost_usd}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-4">
                    Costi stimati in USD in base al listino modelli (non fattura OpenAI).
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-500">Nessun dato disponibile.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-rose-600" />
                Attività ricerche AI
              </CardTitle>
              <p className="text-sm text-slate-500">
                Elenco delle richieste Matching AI (area, token, esito, costo).
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-3 mb-6">
                <div>
                  <Label>Mese</Label>
                  <Input
                    type="month"
                    className="mt-1 w-44"
                    value={operationsMonth}
                    onChange={(e) => setOperationsMonth(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Funzione</Label>
                  <select
                    className="mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    value={operationsFeature}
                    onChange={(e) => setOperationsFeature(e.target.value)}
                  >
                    <option value="">Tutte</option>
                    <option value="matching">Matching AI</option>
                  </select>
                </div>
                <Button
                  variant="outline"
                  onClick={() =>
                    loadOperations(operationsMonth, operationsFeature, 0)
                  }
                  disabled={operationsLoading}
                >
                  {operationsLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Filtra
                </Button>
              </div>

              {operationsLoading && !operations.items?.length ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : operations.items?.length ? (
                <>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Data</th>
                          <th className="px-3 py-2 font-semibold">Area</th>
                          <th className="px-3 py-2 font-semibold">Risultati</th>
                          <th className="px-3 py-2 font-semibold">Token</th>
                          <th className="px-3 py-2 font-semibold">Costo</th>
                          <th className="px-3 py-2 font-semibold">Esito</th>
                        </tr>
                      </thead>
                      <tbody>
                        {operations.items.map((row) => (
                          <tr
                            key={row.id}
                            className="border-t border-slate-100 hover:bg-slate-50/80"
                          >
                            <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                              {formatWhen(row.created_at)}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-slate-800">
                                {row.area_ricerca || "—"}
                              </div>
                              <div className="text-xs text-slate-400">
                                {featureLabel(row.feature)} · {row.model}
                                {row.servizi_richiesti?.length
                                  ? ` · ${row.servizi_richiesti.join(", ")}`
                                  : ""}
                              </div>
                            </td>
                            <td className="px-3 py-2 tabular-nums text-slate-600">
                              {row.results_count ?? "—"}
                              {row.candidates_count != null && (
                                <span className="text-xs text-slate-400">
                                  {" "}
                                  / {row.candidates_count} cand.
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 tabular-nums text-slate-600">
                              {(row.total_tokens ?? 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 tabular-nums text-slate-600">
                              ${(row.estimated_cost_usd ?? 0).toFixed(4)}
                            </td>
                            <td className="px-3 py-2">
                              {row.success ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  OK
                                </Badge>
                              ) : (
                                <Badge variant="destructive">Errore</Badge>
                              )}
                              {row.error && (
                                <p className="text-xs text-rose-600 mt-1 max-w-[220px] truncate" title={row.error}>
                                  {row.error}
                                </p>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
                    <span>
                      {operations.total} operazioni
                      {operations.month ? ` · mese ${operations.month}` : ""}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          operationsLoading || (operations.offset || 0) <= 0
                        }
                        onClick={() =>
                          loadOperations(
                            operationsMonth,
                            operationsFeature,
                            Math.max(0, (operations.offset || 0) - (operations.limit || 50))
                          )
                        }
                      >
                        Precedenti
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          operationsLoading ||
                          (operations.offset || 0) + (operations.limit || 50) >=
                            (operations.total || 0)
                        }
                        onClick={() =>
                          loadOperations(
                            operationsMonth,
                            operationsFeature,
                            (operations.offset || 0) + (operations.limit || 50)
                          )
                        }
                      >
                        Successive
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500 py-6 text-center">
                  Nessuna ricerca AI registrata{operationsMonth ? ` per ${operationsMonth}` : ""}.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="future" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center">
              <Home className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-slate-800">Prossimamente</h3>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                Qui andranno altre funzioni di backend per CasaDiRiposoPerTe
                (sync Zoho, richieste, pagamenti, ecc.), gestite da questo pannello Affidya.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
